import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CustomerDownloadData, DocumentDataModel } from 'store/types/Customer';
import CardComponent from 'components/common/CardComponent';
import { CustomerDownloadService } from 'services/CustomerDownloadService';
import { HttpError } from 'services/HttpService';
import { HTTP_ERROR_CODE } from 'store/types/HttpTypes';
import { conf } from 'config';
import CardFieldRowComponent from 'components/common/CardFieldRowComponent';
import TableComponent from 'components/common/TableComponent';
import ErrorComponent from 'components/common/ErrorComponent';
import SpinnerComponent from 'components/SpinnerComponent';

import './CustomerDownloadComponent.scss';

const CustomerDownloadComponent: React.FunctionComponent = () => {
  const location = useLocation();
  const param = new URLSearchParams(location.search);
  const [spinnerVisible, setSpinnerVisible] = useState(false);
  const [customerDownloadData, setCustomerDownloadData] = useState<DocumentDataModel | null>(null);
  const [httpError, setHttpError] = useState('');
  const [editCustomerDownloadData, setEditCustomerDownloadData] = useState<CustomerDownloadData>({
    email: param.get('email') || '',
    accessCode: param.get('accessCode') || '',
  });

  const handleChange = useCallback((field: keyof CustomerDownloadData, value: string) => {
    setEditCustomerDownloadData((prev) => ({
      ...prev,
      [field]: value.trim(),
    }));
  }, []);

  const filterCustomerDownload = useCallback(async () => {
    setCustomerDownloadData(null);
    if (editCustomerDownloadData.email && editCustomerDownloadData.accessCode) {
      setHttpError('');
      setSpinnerVisible(true);

      try {
        const response = await CustomerDownloadService.getCustomerDownload(editCustomerDownloadData);
        setCustomerDownloadData(response[0]);
      } catch (err) {
        const httpError = err as HttpError;
        if (httpError.code === HTTP_ERROR_CODE.UNAUTHORIZED) {
          setHttpError(conf.authErrMessage);
        } else if (httpError.code === HTTP_ERROR_CODE.BAD_REQUEST) {
          setHttpError('No records exist for that email and access code.');
        } else {
          setHttpError(`Error code: ${httpError.code}. Error Message: ${httpError.message}`);
        }
      } finally {
        setSpinnerVisible(false);
      }
    }
  }, [editCustomerDownloadData]);

  return (
    <div id="customerDownloadComponent" className="container mt-3">
      <div className="row">
        <div className="row col-lg-7 col-md-12 col-sm-12">
          <CardComponent title="Product Downloads" buttonText="Submit" onButtonClick={filterCustomerDownload}>
            <div className="row col-12 col-lg-12 mb-3">
              <div className="row col-12 col-lg-12">
                <div className="row col-6 col-lg-6 col-md-12 col-sm-12">
                  <label className="customerDownloadLabel" htmlFor="email">
                    Email Address:
                  </label>
                </div>
                <div className="row col-9 col-lg-7 col-md-12 col-sm-12">
                  <input
                    id="email"
                    name="email"
                    type="text"
                    value={editCustomerDownloadData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="form-control w-100 pl-3 pr-3"
                  />
                </div>
              </div>

              <div className="row col-12 col-lg-12">
                <p></p>
              </div>

              <div className="row col-12 col-lg-12">
                <div className="row col-6 col-lg-6 col-md-12 col-sm-12">
                  <label className="customerDownloadLabel" htmlFor="accessCode">
                    Access Code:
                  </label>
                </div>
                <div className="row col-9 col-lg-7 col-md-12 col-sm-12">
                  <input
                    id="accessCode"
                    name="accessCode"
                    type="text"
                    value={editCustomerDownloadData.accessCode}
                    onChange={(e) => handleChange('accessCode', e.target.value)}
                    className="form-control w-100 pl-3 pr-3"
                  />
                </div>
              </div>
            </div>
          </CardComponent>
        </div>
      </div>

      {httpError && <ErrorComponent message={httpError} />}

      {spinnerVisible ? (
        <div className="mb-4">
          <SpinnerComponent visible={spinnerVisible} />
        </div>
      ) : (
        <div className="card-divider-dashed w-100 mb-3" />
      )}

      <div className="row">
        <div className="col-12 col-lg-12">
          {customerDownloadData?.standards && (
            <CardComponent title="">
              <p>
                The intended user of the SEMI Standards and/or Safety Guidelines file(s) below may either Download or
                View the documents. If you are purchasing the document(s) for another user, please note that SEMI's DRM
                platform monitors the numbers devices used and can accordingly restrict access.
              </p>
              <p>
                SEMI Standards and Safety Guidelines PDFs are encrypted. Downloaded files will only open if Adobe
                Acrobat and the FileOpen Acrobat plug-in are used.
              </p>
              <p>
                For any questions, please visit the{' '}
                <a href="https://www.semi.org/en/products-services/standards/drm-faq" target="_blank" rel="noreferrer">
                  FAQ
                </a>{' '}
                page for more information or{' '}
                <a href="https://discover.semi.org/contact-us.html" target="_blank" rel="noreferrer">
                  contact your local SEMI staff
                </a>
                . To purchase more copies of SEMI Standards, please visit our{' '}
                <a href="https://store-us.semi.org/" target="_blank" rel="noreferrer">
                  e-store
                </a>{' '}
                or learn more{' '}
                <a
                  href="https://www.semi.org/en/products-services/standards/semiviews-online-access-to-semi-standards"
                  target="_blank"
                  rel="noreferrer"
                >
                  SEMIViews
                </a>{' '}
                subscription.
              </p>
            </CardComponent>
          )}
        </div>

        <div className="col-12 col-lg-12">
          {customerDownloadData ? (
            <>
              <h4 className="mb-3">
                <strong>Important Announcement:</strong>
              </h4>
              <p>
                To ensure the continued performance and security of our digital content platform, purchases older than
                five (5) years will be archived and removed from online access. We encourage you to download and save
                any content you wish to retain before it reaches the archival period (December 15, 2026).{' '}
              </p>
              <hr />
            </>
          ) : (
            <p></p>
          )}
        </div>

        <div className="col-12 col-lg-12">
          <CardComponent title="">
            {customerDownloadData ? (
              <TableComponent value={customerDownloadData.documentDownload} />
            ) : (
              <>
                <CardFieldRowComponent subtitle={'Please enter email and access code above.'} />
                <div className="card-divider-dashed w-100 mb-3" />
              </>
            )}
          </CardComponent>
        </div>
      </div>
    </div>
  );
};

export default CustomerDownloadComponent;
